import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ContentSection, Form, PageWrap } from '@vwfs-bronson/bronson-react';
import { Formik, useFormikContext } from 'formik';
import { BeforeSubmitHandler } from '..';
import { getBrandName } from '../../../config';
import { Analytics } from '../../../services/analytics';
import { productConfigurationEvent } from '../../../services/analytics/analyticsEvents';
import { confirmationQuote } from '../../../services/api/miles/miles-services';
import { assertDefined, useScrollToFormError } from '../../../services/common/miscUtils';
import { SetState } from '../../../services/common/miscUtils.types';
import { customerSelectors } from '../../../services/redux/features/customer.slice';
import { loadingActions } from '../../../services/redux/features/loading.slice';
import { quoteSelectors, updateQuoteIdAndStatus } from '../../../services/redux/features/quote.slice';
import { useNavigate } from '../../../services/routing/navigate';
import { QuotationFormData } from '../../../types';
import { ConfirmationModal } from './ConfirmationModal';
import { FormNavigationButtons } from './FormNavigationButtons';
import { getInitialValues } from './initial-values';
import { QuotationFinancialProductForm } from './QuotationFinancialProductForm';

interface FormStepProps {
  setStepOnSubmit: SetState<BeforeSubmitHandler>;
}

const FormStep: FunctionComponent<FormStepProps> = (props) => {
  const { setStepOnSubmit } = props;
  const { errors, isValidating, isSubmitting, handleReset, handleSubmit, submitForm } =
    useFormikContext<QuotationFormData>();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const quoteIsSavedOrValidated = useSelector(quoteSelectors.selectIsQuoteSavedOrValidated);

  const hasCoCustomer = useSelector(customerSelectors.selectHasCoCustomer);
  const customerType = assertDefined(useSelector(customerSelectors.selectCustomerType));
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);

  useEffect(() => {
    document.title = t('step3:title');
  }, [t]);

  useScrollToFormError(isValidating, isSubmitting, errors);

  React.useEffect(() => {
    Analytics.push(productConfigurationEvent(customerType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const quoteId = assertDefined(useSelector(quoteSelectors.selectQuoteId));

  const quoteWasAlreadyApproved = useSelector(quoteSelectors.selectQuoteWasAlreadyApproved);

  useEffect(() => {
    const onSubmit = async () => {
      if (quoteWasAlreadyApproved) return true;

      try {
        dispatch(loadingActions.setLoading(true));
        const quoteFragment = await confirmationQuote(quoteId);
        dispatch(updateQuoteIdAndStatus(quoteFragment));

        return true;
      } catch {
        navigate.error();
        return false;
      } finally {
        dispatch(loadingActions.setLoading(false));
      }
    };

    setStepOnSubmit(() => onSubmit);

    return () => {
      setStepOnSubmit(() => () => true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerType, navigate, quoteId, setStepOnSubmit, quoteWasAlreadyApproved]);

  const setConfirmationModalShown = (isVisible: boolean) => {
    setShowConfirmationModal(isVisible);
  };

  const handleConfirm = async () => {
    setConfirmationModalShown(false);
    await submitForm();
  };

  return (
    <PageWrap>
      <Form onReset={handleReset} onSubmit={handleSubmit}>
        <ContentSection altBackground={getBrandName().includes('vw')} className="u-pv-none">
          <QuotationFinancialProductForm quoteId={quoteId} />
          <ConfirmationModal
            shown={showConfirmationModal}
            onConfirm={handleConfirm}
            onClose={() => setConfirmationModalShown(false)}
          />
          <FormNavigationButtons
            disableForward={!quoteIsSavedOrValidated}
            canGoForward
            canGoBack
            forwardName={t('step3:navigation:next')}
            backName={t('step3:navigation:return')}
            goBackHandler={() => {
              if (hasCoCustomer) {
                navigate.form(2, '/coContractor');
              } else {
                navigate.form('/2');
              }
            }}
            goForwardHandler={() => (quoteWasAlreadyApproved ? submitForm() : setConfirmationModalShown(true))}
          />
        </ContentSection>
      </Form>
    </PageWrap>
  );
};

interface StepProps {
  onSubmit: (values: QuotationFormData) => void;
  setStepOnSubmit: SetState<BeforeSubmitHandler>;
}

export const Step: FunctionComponent<StepProps> = ({ onSubmit, setStepOnSubmit }) => {
  const customerType = assertDefined(useSelector(customerSelectors.selectCustomerType));

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(customerType)}
      validationSchema={(() => {
        // if (customerType === CustomerType.BUSINESS) return null; /* temporarily skipped */
        // return ValidationSchema(productType === FinancialProductType.STANDALONE);
        // TODO: fix validation issues;
        return null;
      })()}
      onSubmit={onSubmit}
      validateOnBlur
    >
      <FormStep setStepOnSubmit={setStepOnSubmit} />
    </Formik>
  );
};
